import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TimeCardEntry } from './TimeCard.types.ts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DB, Schemas } from '@tonic/central-specialties-utils';
import {
  Alert,
  AlertText,
  Center,
  Icons,
  Modal,
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  Spinner,
  Text,
  useAlert,
  VStack,
} from '@tonic/central-specialties-ui-themed';
import { Button, Icon, ScrollView } from '@gluestack-ui/themed';
import { Form, FormFieldsObject } from '../general/Form/Form.tsx';
import { TimeframeData } from '../general/Form/FormField/specific/TimeframeFormField.tsx';
import moment from 'moment-timezone';
import { useFormJobOptions } from '../general/Form/hooks/useFormJobOptions.ts';
import { useFormJobCodeOptions } from '../general/Form/hooks/useFormJobCodeOptions.ts';

interface SplitTimeCardEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  timeCardEntry: TimeCardEntry;
  refetch?: () => void;
}

export const SplitTimeCardEntryModal = ({
  isOpen,
  onClose,
  timeCardEntry,
  refetch,
}: SplitTimeCardEntryModalProps) => {
  const { alert } = useAlert();

  const targetDate = useMemo(
    () => moment.utc(timeCardEntry.startTime).tz('America/Chicago').toDate(),
    [timeCardEntry.startTime],
  );

  const { jobOptions } = useFormJobOptions();
  const {
    laborCodeOptions,
    divisionCodeOptions,
    costCodeOptions,
    extractCostCodeOptions,
    isPending: jobCodeOptionsPending,
  } = useFormJobCodeOptions();

  const [selectedJobId, setSelectedJobId] = useState<string | null>(
    timeCardEntry.job?.id ?? null,
  );
  const [selectedDivisionCodeId, setSelectedDivisionCodeId] = useState<
    string | null
  >(timeCardEntry.divisionCode?.id ?? null);
  const [selectedLaborCodeId, setSelectedLaborCodeId] = useState<string | null>(
    timeCardEntry.laborCode?.id ?? null,
  );
  const [enteredEquipmentNumber, setEnteredEquipmentNumber] = useState<string>(
    timeCardEntry.equipmentNumber ?? '',
  );
  const [equipmentEntityPending, setEquipmentEntityPending] = useState(false);
  const [enteredEquipmentEntity, setEnteredEquipmentEntity] = useState<
    Schemas['EquipmentItemResponse'] | null
  >(null);
  const [splitErrors, setSplitErrors] = useState<string[]>([]);

  const { data: equipmentOptions, isPending: equipmentPending } = useQuery({
    queryKey: ['equipmentOptionsFor', selectedLaborCodeId],
    queryFn: () =>
      DB.GET('/equipment/labor-code/{laborCodeId}', {
        params: { path: { laborCodeId: selectedLaborCodeId } },
      }).then(
        (res: { data: Schemas['EquipmentListResponse'] }) => res.data.equipment,
      ),
    enabled: !!selectedLaborCodeId,
  });

  const fetchEquipmentByNumber = useCallback(
    async (equipmentNumber: string) => {
      setEquipmentEntityPending(true);
      return await DB.GET('/equipment/by-number/{number}', {
        params: { path: { number: equipmentNumber } },
      })
        .then((res: { data: Schemas['EquipmentItemResponse'] }) => {
          setEquipmentEntityPending(false);
          return res?.data ?? null;
        })
        .catch((e) => {
          console.error('fetchEquipmentByNumber failed:', e);
          setEquipmentEntityPending(false);
          return null;
        });
    },
    [],
  );

  useEffect(() => {
    if (enteredEquipmentNumber) {
      const matchingEquipment =
        Array.isArray(equipmentOptions) && equipmentOptions.length > 0
          ? equipmentOptions.find((eq) => eq.number === enteredEquipmentNumber)
          : undefined;

      if (matchingEquipment) {
        setEquipmentEntityPending(false);
        setEnteredEquipmentEntity(matchingEquipment);
      } else {
        fetchEquipmentByNumber(enteredEquipmentNumber).then(
          (res: Schemas['EquipmentItemResponse'] | null) => {
            setEnteredEquipmentEntity(res);
          },
        );
      }
    } else {
      setEquipmentEntityPending(false);
      setEnteredEquipmentEntity(null);
    }
  }, [equipmentOptions, enteredEquipmentNumber, fetchEquipmentByNumber]);

  // Seed cost code options from the source entry's division code
  useEffect(() => {
    if (timeCardEntry.divisionCode?.id) {
      extractCostCodeOptions(timeCardEntry.divisionCode.id);
    }
  }, [extractCostCodeOptions, timeCardEntry.divisionCode?.id]);

  const { mutateAsync: doSplit } = useMutation<
    any,
    any,
    Record<string, unknown>
  >({
    mutationFn: (body: Record<string, unknown>) =>
      // Uses a template-literal path until yarn pull:types regenerates typed paths
      (DB.POST as any)(`/time-card-entries/${timeCardEntry.id}/split`, {
        body,
      }),
  });

  const joinTimeframeDataToDate = useCallback(
    ({ startTime, endTime }: TimeframeData): TimeframeData => {
      const baseDate = moment.tz(targetDate, 'America/Chicago');
      const dateString = baseDate.format('YYYY-MM-DD');
      const startDateTime = moment
        .tz(`${dateString}T${startTime}`, 'America/Chicago')
        .toISOString();
      let endDateTime: string;
      if (endTime === '00:00') {
        const nextDateString = baseDate
          .clone()
          .add(1, 'day')
          .format('YYYY-MM-DD');
        endDateTime = moment
          .tz(`${nextDateString}T${endTime}`, 'America/Chicago')
          .toISOString();
      } else {
        endDateTime = moment
          .tz(`${dateString}T${endTime}`, 'America/Chicago')
          .toISOString();
      }
      return { startTime: startDateTime, endTime: endDateTime };
    },
    [targetDate],
  );

  const defaults = useMemo(
    () => ({
      startTime: moment
        .utc(timeCardEntry.startTime)
        .tz('America/Chicago')
        .format('HH:mm'),
      endTime: '',
      jobId: timeCardEntry.job?.id ?? '',
      laborCodeId: timeCardEntry.laborCode?.id ?? '',
      isRepair: timeCardEntry.isRepair ?? false,
      divisionCodeId: timeCardEntry.divisionCode?.id ?? '',
      costCodeId: timeCardEntry.costCode?.id ?? '',
      equipmentNumber: timeCardEntry.equipmentNumber ?? '',
      hoursMiles: (timeCardEntry as any).hoursMiles ?? '',
      workDescription: timeCardEntry.workDescription ?? '',
      isNoWorkEntry: timeCardEntry.isNoWorkEntry ?? false,
      noWorkType: timeCardEntry.noWorkType ?? '',
    }),
    // Re-initialize only when the source entry changes identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timeCardEntry.id],
  );

  const submit = useCallback(
    async (data: typeof defaults) => {
      setSplitErrors([]);

      const { startTime: startUtc, endTime: endUtc } = joinTimeframeDataToDate({
        startTime: data.startTime,
        endTime: data.endTime,
      });

      const equipmentId =
        enteredEquipmentEntity?.id ??
        (Array.isArray(equipmentOptions)
          ? equipmentOptions.find((eq) => eq.number === data.equipmentNumber)
              ?.id
          : undefined) ??
        null;

      const body: Record<string, unknown> = {
        startTime: startUtc,
        endTime: endUtc,
      };

      if (data.jobId !== (timeCardEntry.job?.id ?? ''))
        body.splitJobId = data.jobId || null;
      if (data.laborCodeId !== (timeCardEntry.laborCode?.id ?? ''))
        body.splitLaborCodeId = data.laborCodeId || null;
      if (data.divisionCodeId !== (timeCardEntry.divisionCode?.id ?? ''))
        body.splitDivisionCodeId = data.divisionCodeId || null;
      if (data.costCodeId !== (timeCardEntry.costCode?.id ?? ''))
        body.splitCostCodeId = data.costCodeId || null;
      if (data.equipmentNumber !== (timeCardEntry.equipmentNumber ?? ''))
        body.splitEquipmentId = equipmentId;
      if (data.hoursMiles !== ((timeCardEntry as any).hoursMiles ?? ''))
        body.splitHoursMiles = data.hoursMiles || null;
      if (data.workDescription !== timeCardEntry.workDescription)
        body.splitWorkDescription = data.workDescription || null;
      if (data.isRepair !== (timeCardEntry.isRepair ?? false))
        body.splitIsRepair = data.isRepair;
      if (data.isNoWorkEntry !== (timeCardEntry.isNoWorkEntry ?? false))
        body.splitIsNoWorkEntry = data.isNoWorkEntry;
      if (data.noWorkType !== (timeCardEntry.noWorkType ?? ''))
        body.splitNoWorkType = data.noWorkType || null;

      try {
        const { error } = await doSplit(body);
        if (error) {
          console.error('Split time card error:', error);
          throw error;
        }
        alert({
          status: 'success',
          message: 'Time card entry split successfully',
          timeout: 5000,
        });
        refetch?.();
        onClose();
        return {};
      } catch (e: any) {
        console.error('Split time card entry failed:', e);
        if (
          'details' in e &&
          Array.isArray(e.details) &&
          e.details.length > 0
        ) {
          setSplitErrors(e.details);
          return { SUBMIT: e.details[0] };
        }
        return {
          SUBMIT: 'An error occurred while splitting the time card entry.',
        };
      }
    },
    [
      joinTimeframeDataToDate,
      enteredEquipmentEntity,
      equipmentOptions,
      timeCardEntry,
      doSplit,
      alert,
      refetch,
      onClose,
    ],
  );

  const sourceTimeRange = useMemo(() => {
    const start = moment
      .utc(timeCardEntry.startTime)
      .tz('America/Chicago')
      .format('h:mma');
    const end = timeCardEntry.endTime
      ? moment.utc(timeCardEntry.endTime).tz('America/Chicago').format('h:mma')
      : null;
    return end ? `${start} – ${end} CST` : `${start} CST`;
  }, [timeCardEntry.startTime, timeCardEntry.endTime]);

  const formFields = useMemo(
    () => ({
      duration: {
        type: 'Timeframe',
        dataFieldKey: ['startTime', 'endTime'],
        label: 'Split range',
        required: true,
        validator: (timeframeData: TimeframeData) => {
          if (!timeframeData?.startTime || !timeframeData?.endTime) {
            return 'Start and end times are required';
          }
          const { startTime: splitStart, endTime: splitEnd } =
            joinTimeframeDataToDate(timeframeData);
          const start = moment(splitStart);
          const end = moment(splitEnd);
          const sourceStart = moment(timeCardEntry.startTime);
          const sourceEnd = moment(timeCardEntry.endTime);

          if (start.isAfter(end)) return 'Start time must be before end time';
          if (start.isSame(end))
            return 'Start and end times cannot be the same';
          if (start.minute() % 15 !== 0 || end.minute() % 15 !== 0)
            return 'Times must be in 15 minute increments';
          if (start.isBefore(sourceStart) || end.isAfter(sourceEnd))
            return "Split range must fall within the source entry's time bounds";
          if (start.isSame(sourceStart) && end.isSame(sourceEnd))
            return 'Split range cannot span the entire source entry';
        },
      },

      isNoWorkEntry: {
        type: 'Checkbox',
        dataFieldKey: 'isNoWorkEntry',
        label: 'No work',
        size: 'lg',
      },

      noWorkType: {
        type: 'Select',
        dataFieldKey: 'noWorkType',
        label: 'No work type',
        placeholder: 'Select a no-work type',
        options: {
          'PTO: request in Bamboo': 'PTO',
          Holiday: 'Holiday',
          'ESST: request in Bamboo': 'ESST',
          'Rain Day: request in Bamboo if you wish to use ESST': 'RainDay',
          'Day Off': 'DayOff',
        },
        hidden: (formData: any) => !formData.isNoWorkEntry,
      },

      jobNumber: {
        type: 'Select',
        dataFieldKey: 'jobId',
        label: 'Job number',
        placeholder: 'Select job number',
        options: jobOptions,
        hidden: (data: any) => data.isNoWorkEntry === true,
        onChange: (val: string) => setSelectedJobId(val),
      },

      laborCodeId: {
        type: 'Select',
        dataFieldKey: 'laborCodeId',
        label: 'Labor code',
        placeholder: 'Select labor code',
        options: laborCodeOptions || {},
        hidden: (data: any) => data.isNoWorkEntry === true,
        disabled: !selectedJobId,
        onChange: (val: string) => setSelectedLaborCodeId(val),
        pending: !!selectedJobId && jobCodeOptionsPending,
      },

      isRepair: {
        type: 'Radio',
        options: { Yes: true, No: false },
        dataFieldKey: 'isRepair',
        label: 'Repair?',
        hidden: (data: any) => data.isNoWorkEntry === true,
      },

      divisionCodeId: {
        type: 'Select',
        dataFieldKey: 'divisionCodeId',
        label: 'Division code',
        placeholder: 'Select division code',
        options: divisionCodeOptions || {},
        hidden: (data: any) => data.isNoWorkEntry === true,
        disabled: !selectedJobId,
        onChange: (item: string) => {
          extractCostCodeOptions(item);
          setSelectedDivisionCodeId(item);
        },
        pending: !!selectedJobId && jobCodeOptionsPending,
      },

      costCodeId: {
        type: 'Select',
        dataFieldKey: 'costCodeId',
        label: 'Cost code',
        placeholder: 'Select cost code',
        options: costCodeOptions || {},
        hidden: (data: any) => data.isNoWorkEntry === true,
        disabled: !selectedDivisionCodeId,
        pending: !!selectedDivisionCodeId && jobCodeOptionsPending,
      },

      equipmentNumber: {
        type: 'Text',
        dataFieldKey: 'equipmentNumber',
        label: 'Equipment number',
        placeholder: 'Enter equipment number',
        hidden: (data: any) => data.isNoWorkEntry === true,
        onChange: setEnteredEquipmentNumber,
        pending:
          equipmentEntityPending || (!!selectedLaborCodeId && equipmentPending),
      },

      hoursMiles: {
        type: 'Numeric',
        dataFieldKey: 'hoursMiles',
        label: 'Hours/Miles',
        placeholder: 'Hours/Miles',
        hidden: (data: any) =>
          data.isNoWorkEntry === true || !enteredEquipmentEntity,
      },

      workDescription: {
        type: 'Text',
        dataFieldKey: 'workDescription',
        label: 'Work description',
        multiline: true,
        placeholder: 'Describe the work completed',
        hidden: (data: any) => data.isNoWorkEntry === true,
      },
    }),
    [
      jobOptions,
      laborCodeOptions,
      selectedJobId,
      jobCodeOptionsPending,
      divisionCodeOptions,
      costCodeOptions,
      selectedDivisionCodeId,
      selectedLaborCodeId,
      equipmentEntityPending,
      equipmentPending,
      enteredEquipmentEntity,
      extractCostCodeOptions,
      joinTimeframeDataToDate,
    ],
  );

  if (jobCodeOptionsPending) {
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalBackdrop />
        <ModalContent
          borderRadius={0}
          maxHeight="80%"
          width="60%"
          maxWidth={1000}
          minWidth={500}
        >
          <Center h="$64">
            <Spinner />
          </Center>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalBackdrop />
      <ModalContent
        borderRadius={0}
        maxHeight="80%"
        w="$96"
        width="60%"
        maxWidth={1000}
        minWidth={500}
      >
        <ModalHeader
          bg="$primary700"
          alignItems="center"
          justifyContent="space-between"
          py="$2"
          pl="$10"
          pr="$6"
        >
          <Text color="$white" size="lg">
            Split entry — {sourceTimeRange}
          </Text>
          <Button w="$12" minWidth="auto" onPress={onClose}>
            <Icon color="$white" size="xl" as={Icons.X} />
          </Button>
        </ModalHeader>
        <ScrollView>
          <VStack p="$6" space="md">
            {splitErrors.length > 0 && (
              <Alert
                action="error"
                variant="outline"
                flexDirection="column"
                gap="$1"
                p="$4"
              >
                {splitErrors.map((err, i) => (
                  <AlertText key={i}>• {err}</AlertText>
                ))}
              </Alert>
            )}
            <Form
              key={`split-${timeCardEntry.id}`}
              formData={defaults}
              fields={formFields as unknown as FormFieldsObject}
              onSubmit={submit as any}
              submitButtonText="Split Entry"
            />
          </VStack>
        </ScrollView>
      </ModalContent>
    </Modal>
  );
};
